import React, { useState, useEffect, useRef } from "react";
import { Modal, Text, TouchableOpacity, View, Image, Animated, Dimensions, Platform } from "react-native";
import { AppColors } from "../../../../utility/AppColors";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import Ionicons from "@expo/vector-icons/Ionicons";
import { useNotificationDetailsPopupStyles } from "../Notifications/NotificationPopup/notificationDetailsPopupStyles";
import Avatar from "../../../../Components/choose-avatar-components/Avatar";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import { LinearGradient } from "expo-linear-gradient";

const { height: screenHeight } = Dimensions.get("window");

export function WelcomePopup(props) {
  const {
    showModal = false,
    setShowModal,
    onClose,
    username = "User",
    profilePicture = null,
    koCoins = 100,
    showRedirectCountdown = false,
    onManualRedirect = null,
    flowType = null,
    notificationAction = null,
  } = props;

  const [countdown, setCountdown] = useState(10);
  const styles = useNotificationDetailsPopupStyles();
  const deviceSize = useDeviceSize();
  const isMobile = deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;
  const slideAnim = useRef(new Animated.Value(screenHeight * 0.6)).current;

  useEffect(() => {
    if (showModal && showRedirectCountdown && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (showModal && showRedirectCountdown && countdown === 0) {
      if (onManualRedirect) {
        onManualRedirect();
      }
    }
  }, [showModal, showRedirectCountdown, countdown, onManualRedirect]);

  useEffect(() => {
    if (showModal) {
      setCountdown(10);
      // Animate slide up on mobile
      if (isMobile) {
        Animated.spring(slideAnim, {
          toValue: 0,
          useNativeDriver: true,
          tension: 65,
          friction: 10,
        }).start();
      }
    } else {
      // Animate slide down on mobile
      if (isMobile) {
        Animated.timing(slideAnim, {
          toValue: screenHeight * 0.6,
          duration: 250,
          useNativeDriver: true,
        }).start();
      }
    }
  }, [showModal, isMobile]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    setShowModal(false);
  };

  const handleRedirectNow = () => {
    if (onManualRedirect) {
      onManualRedirect();
    }
  };

  const getWelcomeMessages = () => {
    if (flowType === "gameshow") {
      return {
        firstMessage: `Congratulations on completing the sign up! You have earned ${koCoins} KO coins`,
        secondMessage: "You're all set to dive into the action.",
        redirectMessage: "Redirecting you to the event lobby in",
        buttonText: "Go Now",
      };
    } else if (flowType === "notification" && notificationAction) {
      const actionMessages = {
        negotiate: {
          firstMessage: `Congratulations on completing the sign up! You have earned ${koCoins} KO coins`,
          secondMessage:
            "You're all set to dive into the action. Negotiate with the organizer",
          redirectMessage: "Redirecting you to challenge to negotiate in",
          buttonText: "Go Now",
        },
        accept: {
          firstMessage: `Congratulations on completing the sign up! You have earned ${koCoins} KO coins`,
          secondMessage: "You're all set to dive into the action.",
          redirectMessage: "Redirecting you to challenge to accept in",
          buttonText: "Go Now",
        },
        reject: {
          firstMessage: `Congratulations on completing the sign up! You have earned ${koCoins} KO coins`,
          secondMessage: "You're all set to dive into the action.",
          redirectMessage: "Redirecting you to challenge to reject in",
          buttonText: "Go Now",
        },
      };
      return actionMessages[notificationAction] || actionMessages.negotiate;
    } else {
      return {
        firstMessage: "Congratulations on completing the setup!",
        secondMessage: "You're all set to dive into the action.",
        redirectMessage: null,
        buttonText: "Continue",
      };
    }
  };

  const messages = getWelcomeMessages();

  // Mobile bottom drawer content
  const renderMobileContent = () => (
    <Modal
      animationType="none"
      transparent={true}
      visible={showModal}
      onRequestClose={handleClose}
    >
      <View style={{ flex: 1, backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
        <TouchableOpacity
          style={{ flex: 1 }}
          onPress={handleClose}
          activeOpacity={1}
        />
        <Animated.View
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 430,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            borderWidth: 1,
            borderBottomWidth: 0,
            borderColor: "#DC2626",
            transform: [{ translateY: slideAnim }],
            overflow: "hidden",
            // Box shadow
            shadowColor: "rgba(232, 232, 232, 0.15)",
            shadowOffset: { width: 0, height: -3 },
            shadowOpacity: 1,
            shadowRadius: 14,
            elevation: 10,
          }}
        >
          <LinearGradient
            colors={["#111827", "#000000"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              flex: 1,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              paddingVertical: 15,
              paddingHorizontal: 12,
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 12,
            }}
          >
            {/* Header */}
            <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_20,
                  { color: AppColors.white },
                ]}
              >
                All set!
              </Text>
              <TouchableOpacity onPress={handleClose}>
                <View style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: "rgba(255,255,255,0.1)", justifyContent: "center", alignItems: "center" }}>
                  <Ionicons name="close" size={18} color={AppColors.white} />
                </View>
              </TouchableOpacity>
            </View>

            {/* Separator line */}
            <View style={{ height: 1, backgroundColor: "#838383", width: "100%" }} />

            {/* Content */}
            <View style={{ alignItems: "center", gap: 12, width: "100%", flex: 1, justifyContent: "center" }}>
              {/* Avatar with border */}
              <View
                style={{
                  width: 119,
                  height: 119,
                  borderRadius: 11,
                  borderWidth: 0.66,
                  borderColor: "#D2D2D2",
                  overflow: "hidden",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#1F2937",
                }}
              >
                <Avatar
                  src={profilePicture}
                  variant="full"
                  changeAvatar={true}
                />
              </View>

              {/* Welcome Message */}
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_24,
                  { color: AppColors.white, textAlign: "center" },
                ]}
              >
                Welcome {username}
              </Text>

              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  { color: AppColors.white, textAlign: "center", lineHeight: 20 },
                ]}
              >
                {messages.firstMessage}
              </Text>

              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  { color: AppColors.white, textAlign: "center", lineHeight: 20, marginBottom: showRedirectCountdown ? 8 : 20 },
                ]}
              >
                {messages.secondMessage}
              </Text>

              {/* Countdown and Redirect */}
              {showRedirectCountdown && (
                <View style={{ alignItems: "center", gap: 12, marginBottom: 20 }}>
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_12,
                      { color: AppColors.yellow_b69200, textAlign: "center" },
                    ]}
                  >
                    {messages.redirectMessage} {countdown} seconds...
                  </Text>

                  <TouchableOpacity
                    style={{
                      backgroundColor: AppColors.appViolentColor_6D08C3,
                      paddingHorizontal: 24,
                      paddingVertical: 10,
                      borderRadius: 8,
                    }}
                    onPress={handleRedirectNow}
                  >
                    <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: AppColors.white }]}>
                      {messages.buttonText}
                    </Text>
                  </TouchableOpacity>
                </View>
              )}

              {/* Explore Events button (when no redirect countdown) */}
              {!showRedirectCountdown && (
                <TouchableOpacity
                  style={{
                    backgroundColor: AppColors.appViolentColor_6D08C3,
                    paddingHorizontal: 32,
                    paddingVertical: 12,
                    borderRadius: 8,
                    marginTop: 8,
                  }}
                  onPress={handleClose}
                >
                  <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: AppColors.white }]}>
                    Explore Events
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </LinearGradient>
        </Animated.View>
      </View>
    </Modal>
  );

  // Desktop centered modal content
  const renderDesktopContent = () => (
    <Modal
      animationType="fade"
      transparent={true}
      visible={showModal}
      onRequestClose={handleClose}
    >
      <View style={styles.container}>
        <View style={[styles.mainView, { maxWidth: 700, minHeight: "auto" }]}>
          <View style={styles.header}>
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_24,
                { color: AppColors.white, flex: 1 },
              ]}
            >
              All set!
            </Text>
            <TouchableOpacity onPress={handleClose}>
              <View style={styles.closeIconWrapper}>
                <Ionicons name="close" size={20} color={AppColors.white} />
              </View>
            </TouchableOpacity>
          </View>

          <View style={styles.separator} />

          {/* Content */}
          <View style={{ alignItems: "center", paddingVertical: 40, paddingHorizontal: 20 }}>
            {/* Avatar */}
            <View style={{ marginBottom: 24 }}>
              <Avatar
                src={profilePicture}
                variant="full"
                style={{ width: 80, height: 80 }}
              />
            </View>

            {/* Welcome Message */}
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_32,
                { color: AppColors.white, textAlign: "center", marginBottom: 20 },
              ]}
            >
              Welcome {username}
            </Text>

            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_16,
                { color: AppColors.white, textAlign: "center", lineHeight: 24, marginBottom: 8 },
              ]}
            >
              {messages.firstMessage}
            </Text>

            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_16,
                { color: AppColors.white, textAlign: "center", lineHeight: 24, marginBottom: showRedirectCountdown ? 20 : 0 },
              ]}
            >
              {messages.secondMessage}
            </Text>

            {/* Countdown and Redirect */}
            {showRedirectCountdown && (
              <View style={{ alignItems: "center", marginBottom: 20 }}>
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_14,
                    { color: AppColors.yellow_b69200, textAlign: "center", marginBottom: 12 },
                  ]}
                >
                  {messages.redirectMessage} {countdown} seconds...
                </Text>

                <TouchableOpacity
                  style={[
                    styles.acceptRejectButtonStyle,
                    {
                      backgroundColor: AppColors.appViolentColor_6D08C3,
                      paddingHorizontal: 24,
                      paddingVertical: 8,
                      minWidth: 120,
                    },
                  ]}
                  onPress={handleRedirectNow}
                >
                  <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: AppColors.white }]}>
                    {messages.buttonText}
                  </Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );

  return isMobile ? renderMobileContent() : renderDesktopContent();
}

export default WelcomePopup;

